// src/pages/card/CenteringTool.tsx
import React, { useState, useEffect, useRef } from "react";
import Axios from "axios";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Rnd } from "react-rnd";

type Side = "front" | "back";

interface BoxPct {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface SideData {
    outer: BoxPct;
    inner: BoxPct;
}

interface CenteringData {
    front: SideData;
    back: SideData;
}

interface CenteringToolProps {
    cardId: number | string;
    frontUrl: string;
    backUrl: string;
}

const defaultBox: BoxPct = {
    x: 0.05,
    y: 0.05,
    width: 0.9,
    height: 0.9,
};

const innerDefaultBox: BoxPct = {
    x: 0.08,
    y: 0.08,
    width: 0.84,
    height: 0.84,
};

const CenteringTool: React.FC<CenteringToolProps> = ({
    cardId,
    frontUrl,
    backUrl,
}) => {
    const [activeSide, setActiveSide] = useState<Side>("front");
    const [data, setData] = useState<CenteringData>({
        front: { outer: defaultBox, inner: innerDefaultBox },
        back: { outer: defaultBox, inner: innerDefaultBox },
    });

    const [imageSize, setImageSize] = useState<{ width: number; height: number }>(
        { width: 0, height: 0 }
    );
    const [zoom, setZoom] = useState<number>(1); // 1 = 100%
    const imgRef = useRef<HTMLImageElement | null>(null);

    const LOAD_URL = (id: number | string) =>
        `https://api.alientrader.cards/card/${id}/centering`;
    const SAVE_URL = (id: number | string) =>
        `https://api.alientrader.cards/card/${id}/centering`;

    // Load existing centering data
    useEffect(() => {
        if (!cardId) return;

        Axios.get<CenteringData>(LOAD_URL(cardId))
            .then((res) => {
                if (res.data) {
                    setData(res.data);
                }
            })
            .catch((err) => {
                if (err.response && err.response.status === 404) return;
                console.warn("Error loading centering data:", err);
            });
    }, [cardId]);

    const updateImageSize = () => {
        if (imgRef.current) {
            setImageSize({
                width: imgRef.current.clientWidth,
                height: imgRef.current.clientHeight,
            });
        }
    };

    const handleImageLoad = () => {
        updateImageSize();
    };

    useEffect(() => {
        updateImageSize();
    }, [zoom, activeSide]);

    // pct -> px
    const toPx = (box: BoxPct) => {
        return {
            x: box.x * imageSize.width,
            y: box.y * imageSize.height,
            width: box.width * imageSize.width,
            height: box.height * imageSize.height,
        };
    };

    // px -> pct
    const toPct = (pxBox: {
        x: number;
        y: number;
        width: number;
        height: number;
    }): BoxPct => {
        if (!imageSize.width || !imageSize.height) return defaultBox;
        return {
            x: pxBox.x / imageSize.width,
            y: pxBox.y / imageSize.height,
            width: pxBox.width / imageSize.width,
            height: pxBox.height / imageSize.height,
        };
    };

    const updateBox = (
        side: Side,
        which: "outer" | "inner",
        pxBox: { x: number; y: number; width: number; height: number }
    ) => {
        setData((prev) => ({
            ...prev,
            [side]: {
                ...prev[side],
                [which]: toPct(pxBox),
            },
        }));
    };

    // Calculate border percentages left/right/top/bottom for a side
    const calcCentering = (side: Side) => {
        const { outer, inner } = data[side];

        const left = inner.x - outer.x;
        const right = outer.x + outer.width - (inner.x + inner.width);
        const top = inner.y - outer.y;
        const bottom = outer.y + outer.height - (inner.y + inner.height);

        const horizTotal = left + right || 1;
        const vertTotal = top + bottom || 1;

        const leftPct = (left / horizTotal) * 100;
        const rightPct = (right / horizTotal) * 100;
        const topPct = (top / vertTotal) * 100;
        const bottomPct = (bottom / vertTotal) * 100;

        return {
            leftPct,
            rightPct,
            topPct,
            bottomPct,
            minHoriz: Math.min(leftPct, rightPct),
            minVert: Math.min(topPct, bottomPct),
        };
    };

    const toDisplay = (value: number) => value.toFixed(1);

    const frontStats = calcCentering("front");
    const backStats = calcCentering("back");

    const frontCentering = {
        leftPct: toDisplay(frontStats.leftPct),
        rightPct: toDisplay(frontStats.rightPct),
        topPct: toDisplay(frontStats.topPct),
        bottomPct: toDisplay(frontStats.bottomPct),
    };

    const backCentering = {
        leftPct: toDisplay(backStats.leftPct),
        rightPct: toDisplay(backStats.rightPct),
        topPct: toDisplay(backStats.topPct),
        bottomPct: toDisplay(backStats.bottomPct),
    };

    const noCenteringData =
        JSON.stringify(data.front) ===
        JSON.stringify({ outer: defaultBox, inner: innerDefaultBox }) &&
        JSON.stringify(data.back) ===
        JSON.stringify({ outer: defaultBox, inner: innerDefaultBox });

    const computeOverallPair = (stats: {
        leftPct: number;
        rightPct: number;
        topPct: number;
        bottomPct: number;
    }) => {
        const horizDiff = Math.abs(stats.leftPct - stats.rightPct);
        const vertDiff = Math.abs(stats.topPct - stats.bottomPct);

        // Choose the axis with the larger imbalance
        if (horizDiff >= vertDiff) {
            const hi = Math.max(stats.leftPct, stats.rightPct);
            const lo = Math.min(stats.leftPct, stats.rightPct);
            return { hi, lo };
        } else {
            const hi = Math.max(stats.topPct, stats.bottomPct);
            const lo = Math.min(stats.topPct, stats.bottomPct);
            return { hi, lo };
        }
    };

    const frontOverallPair = computeOverallPair(frontStats);
    const backOverallPair = computeOverallPair(backStats);

    const displayFrontOverallPair = noCenteringData
        ? "-"
        : `${frontOverallPair.hi.toFixed(1)} / ${frontOverallPair.lo.toFixed(1)}`;

    const displayBackOverallPair = noCenteringData
        ? "-"
        : `${backOverallPair.hi.toFixed(1)} / ${backOverallPair.lo.toFixed(1)}`;

    // PSA-style centering caps based on PSA published standards (centering only)
    const computeFrontCenteringGrade = (): number => {
        const h = frontStats.minHoriz;
        const v = frontStats.minVert;

        if (h >= 45 && v >= 45) return 10; // 55/45 or better
        if (h >= 40 && v >= 40) return 9;  // 60/40 or better
        if (h >= 35 && v >= 35) return 8;  // 65/35 or better
        if (h >= 30 && v >= 30) return 7;  // 70/30 or better
        if (h >= 20 && v >= 20) return 6;  // 80/20 or better
        if (h >= 15 && v >= 15) return 5;  // 85/15 or better
        if (h >= 10 && v >= 10) return 3;  // 90/10 or better (PSA 3+ range)
        return 1;                          // Worse than 90/10
    };

    const computeBackCenteringGrade = (): number => {
        const h = backStats.minHoriz;
        const v = backStats.minVert;

        if (h >= 25 && v >= 25) return 10; // 75/25 or better
        if (h >= 10 && v >= 10) return 9;  // 90/10 or better (PSA 9+ range)
        return 1;                          // Worse than 90/10
    };

    const frontCenterGrade = computeFrontCenteringGrade();
    const backCenterGrade = computeBackCenteringGrade();
    const overallCenteringGrade = Math.min(frontCenterGrade, backCenterGrade);

    const displayFrontGrade = noCenteringData ? "-" : `PSA ${frontCenterGrade}`;
    const displayBackGrade = noCenteringData ? "-" : `PSA ${backCenterGrade}`;
    const displayOverallGrade = noCenteringData
        ? "-"
        : `PSA ${overallCenteringGrade}`;


    const handleSave = async () => {
        if (!cardId) return;
        try {
            await Axios.post(SAVE_URL(cardId), data);
            alert("Centering data saved.");
        } catch (err) {
            console.error("Error saving centering data:", err);
            alert("Error saving centering data.");
        }
    };

    const handleResetSide = (side: Side) => {
        setData((prev) => ({
            ...prev,
            [side]: { outer: defaultBox, inner: innerDefaultBox },
        }));
    };

    const sourceUrl = activeSide === "front" ? frontUrl : backUrl;
    const current = data[activeSide];

    return (
        <div className="info-box shadow-sm" style={{ padding: "10px" }}>
            {/* Header */}
            <Row className="mb-2">
                <Col>
                    <h6><strong>Centering Tool</strong></h6>
                </Col>
            </Row>

            {/* PSA-centering-only grades */}
            <Row className="mb-3 justify-content-center text-center">
                <Col md={3}>
                    <div style={{ fontSize: "14px" }}>
                        <strong>Overall:</strong>{" "}
                        <span>{displayOverallGrade}</span>
                    </div>
                </Col>
            </Row>
            <Row className="mb-3 justify-content-center text-center">
                <Col>
                    <div style={{ fontSize: "12px" }}>
                        <strong>Front:</strong>{" "}
                        <span>{displayFrontGrade}</span><br />
                        <span>{displayFrontOverallPair === "-" ? "-" : `${displayFrontOverallPair}`}</span>
                    </div>
                </Col>
                <Col>
                    <div style={{ fontSize: "12px" }}>
                        <strong>Back:</strong>{" "}
                        <span>{displayBackGrade}</span><br />
                        <span>{displayBackOverallPair === "-" ? "-" : `${displayBackOverallPair}`}</span>
                    </div>
                </Col>
            </Row>
            {/* Front / Back Tabs */}
            <Tabs
                id="centering-side-tabs"
                activeKey={activeSide}
                onSelect={(k) => setActiveSide((k as Side) || "front")}
                className="mb-3"
            >
                <Tab eventKey="front" title="Front" />
                <Tab eventKey="back" title="Back" />
            </Tabs>

            {/* Controls */}
            <Row className="mb-2 align-items-center">
                <Col className="justify-content-center text-center">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        style={{ marginRight: "8px" }}
                    >
                        Save Data
                    </Button>
                </Col>
            </Row>
            <Row className="mb-2 align-items-center">
                <Col
                    className="d-flex align-items-center justify-content-md-end mt-2 mt-md-0"
                >
                    <span style={{ fontSize: "12px", marginRight: "8px" }}>Zoom:</span>
                    <input
                        type="range"
                        min={100}
                        max={1000}
                        value={zoom * 100}
                        onChange={(e) => setZoom(Number(e.target.value) / 100)}
                        style={{ flex: 1, maxWidth: "100%" }}
                    />
                    <span
                        style={{ fontSize: "12px", marginLeft: "8px", width: "40px" }}
                    >
                        {Math.round(zoom * 100)}%
                    </span>
                </Col>
            </Row>

            {/* Image + overlays */}
            <Row>
                <Col md={12}>
                    <div
                        style={{
                            maxHeight: "650px",
                            overflow: "auto",
                            border: "1px solid #eee",
                            borderRadius: "4px",
                            padding: "4px",
                        }}
                    >
                        <div
                            style={{
                                position: "relative",
                                display: "inline-block",
                                width: `${zoom * 100}%`,
                                maxWidth: "none",
                            }}
                        >
                            {sourceUrl && (
                                <img
                                    ref={imgRef}
                                    src={sourceUrl}
                                    alt={`${activeSide} scan`}
                                    style={{ width: "100%", height: "auto", borderRadius: "6px" }}
                                    onLoad={handleImageLoad}
                                />
                            )}

                            {imageSize.width > 0 && sourceUrl && (
                                <>
                                    {/* OUTER BOX (resize only, no drag) */}
                                    <Rnd
                                        bounds="parent"
                                        disableDragging={true}
                                        resizeGrid={[1, 1]}
                                        size={toPx(current.outer)}
                                        position={{
                                            x: toPx(current.outer).x,
                                            y: toPx(current.outer).y,
                                        }}
                                        onResizeStop={(_e, _dir, ref, _delta, position) =>
                                            updateBox(activeSide, "outer", {
                                                x: position.x,
                                                y: position.y,
                                                width: ref.offsetWidth,
                                                height: ref.offsetHeight,
                                            })
                                        }
                                        style={{
                                            border: "2px solid rgba(255, 0, 0, 0.6)",
                                            boxSizing: "border-box",
                                            background:
                                                "repeating-linear-gradient(45deg, rgba(255,0,0,0.15), rgba(255,0,0,0.15) 4px, transparent 4px, transparent 8px)",
                                        }}
                                    />

                                    {/* INNER BOX (resize only, no drag) */}
                                    <Rnd
                                        bounds="parent"
                                        disableDragging={true}
                                        resizeGrid={[1, 1]}
                                        size={toPx(current.inner)}
                                        position={{
                                            x: toPx(current.inner).x,
                                            y: toPx(current.inner).y,
                                        }}
                                        onResizeStop={(_e, _dir, ref, _delta, position) =>
                                            updateBox(activeSide, "inner", {
                                                x: position.x,
                                                y: position.y,
                                                width: ref.offsetWidth,
                                                height: ref.offsetHeight,
                                            })
                                        }
                                        style={{
                                            border: "2px solid rgba(255, 255, 255, 0.65)",
                                            boxSizing: "border-box",
                                            background: "rgba(0,0,0,0.05)",
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
            <br /><br />
            {/* Numeric centering stats */}
            <Row className="mb-1 justify-content-center text-center">
                <Col>
                    <Col>
                        <div style={{ fontSize: "11px" }}>
                            <strong>Front</strong>{" "}
                        </div>
                    </Col>
                    <Col>
                        <Row>
                            <div style={{ fontSize: "10px" }}>
                                <span>
                                    L|R: {frontCentering.leftPct}% | {frontCentering.rightPct}%
                                </span>
                            </div>
                        </Row>
                        <div style={{ fontSize: "10px" }}>
                            <span>
                                T|B: {frontCentering.topPct}% |{" "}
                                {frontCentering.bottomPct}%
                            </span>
                        </div>
                    </Col>
                </Col>
                <Col>
                    <Col>
                        <div style={{ fontSize: "11px" }}>
                            <strong>Back</strong>{" "}
                        </div>
                    </Col>
                    <Col>
                        <Row>
                            <div style={{ fontSize: "10px" }}>
                                <span>
                                    L|R: {backCentering.leftPct}% | {backCentering.rightPct}%
                                </span>
                            </div>
                        </Row>
                        <Row>
                            <div style={{ fontSize: "10px" }}>
                                <span>
                                    T|B: {backCentering.topPct}% | {backCentering.bottomPct}%
                                </span>
                            </div>
                        </Row>
                    </Col>
                </Col>
            </Row>
            {/* Reset button at the bottom */}
            <Row className="mt-3">
                <Col className="d-flex justify-content-center">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleResetSide(activeSide)}
                    >
                        Reset {activeSide.toUpperCase()} Boxes
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default CenteringTool;
